import type { Pool } from "../shared/types";

/** A latitude/longitude pair in decimal degrees. */
export interface LatLng {
  lat: number;
  lng: number;
}

/** Meters per mile — for turning haversine distances into a "0.4 mi" label. */
export const METERS_PER_MILE = 1609.34;

/**
 * Mean Earth radius in meters (IUGG). Pools are a few km apart at most, so a
 * spherical (haversine) approximation is far more than accurate enough — no need
 * for an ellipsoidal (Vincenty) model.
 */
const EARTH_RADIUS_M = 6371008.8;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/**
 * Great-circle distance between two points in meters (haversine). Pure and
 * side-effect-free so it can be unit-tested against known DC distances.
 */
export function haversineMeters(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** A pool's distance from `loc`, or Infinity when it has no usable coordinates. */
export function poolDistanceMeters(pool: Pool, loc: LatLng): number {
  if (!Number.isFinite(pool.lat) || !Number.isFinite(pool.lng)) return Infinity;
  return haversineMeters({ lat: pool.lat, lng: pool.lng }, loc);
}

/** Format a meter distance as a 1-decimal mile label, e.g. "0.4 mi away". */
export function milesAwayLabel(meters: number): string {
  return `${(meters / METERS_PER_MILE).toFixed(1)} mi away`;
}

// ---- Session location state -------------------------------------------------
//
// The last successful fix is cached at module scope so List ↔ Splash ↔ Map share
// one grant within a session (no re-prompt as the user moves between tabs). A
// separate localStorage hint records that the user previously opted in, so on a
// LATER visit we can resolve their location silently (the browser won't re-prompt
// once permission is granted) — but we NEVER auto-prompt on a first-ever load.

let lastFix: LatLng | null = null;

/**
 * Why the last `getUserLocation()` attempt failed to produce a fix, so the UI
 * can tailor its banner copy (a "denied" permission needs different, actionable
 * guidance than a transient timeout). Mirrors `lastFix`: set on each failed
 * attempt, cleared back to `null` on success.
 *  - "denied"      — the user (or browser policy) blocked the permission (code 1)
 *  - "unavailable" — position could not be determined (code 2)
 *  - "timeout"     — the request timed out (code 3)
 *  - "unsupported" — no Geolocation API / insecure context (e.g. dev http)
 */
export type LocationError = "denied" | "unavailable" | "timeout" | "unsupported";

let lastError: LocationError | null = null;

/** Key under which we remember the user previously opted into location sorting. */
const OPTIN_KEY = "pt-geo-optin";

/** The last location fix obtained this session, if any. */
export function cachedLocation(): LatLng | null {
  return lastFix;
}

/** Why the last location attempt failed, or null after a success / before any try. */
export function lastLocationError(): LocationError | null {
  return lastError;
}

/** Has the user opted into location sorting before (this or a prior visit)? */
export function hasLocationOptIn(): boolean {
  try {
    return localStorage.getItem(OPTIN_KEY) === "1";
  } catch {
    return false;
  }
}

function rememberOptIn(): void {
  try {
    localStorage.setItem(OPTIN_KEY, "1");
  } catch {
    // Private mode / storage disabled — the in-memory fix still works this session.
  }
}

/**
 * Resolve the user's current location, or `null` if it's unavailable for ANY
 * reason (no Geolocation API, insecure context, permission denied, timeout). It
 * never rejects, so callers can `await` it and simply branch on null — the dev
 * site runs on plain http where the API may be blocked, and the UI must degrade
 * to "unavailable" rather than throw. On success the fix is cached and the opt-in
 * hint is persisted so later visits can resolve silently.
 */
export function getUserLocation(): Promise<LatLng | null> {
  // Reuse this session's fix without re-hitting the API.
  if (lastFix) return Promise.resolve(lastFix);

  const geo =
    typeof navigator !== "undefined" ? navigator.geolocation : undefined;
  if (!geo) {
    // No Geolocation API / insecure context (the dev site runs on plain http).
    lastError = "unsupported";
    return Promise.resolve(null);
  }

  return new Promise<LatLng | null>((resolve) => {
    geo.getCurrentPosition(
      (pos) => {
        lastFix = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        lastError = null;
        rememberOptIn();
        resolve(lastFix);
      },
      (err) => {
        // Map GeolocationPositionError.code: 1=denied, 2=unavailable, 3=timeout.
        lastError =
          err.code === 1 ? "denied" : err.code === 3 ? "timeout" : "unavailable";
        resolve(null); // degrade gracefully — never reject
      },
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 5 * 60_000 },
    );
  });
}

/**
 * Order pools for display. Pure and stable so it can be unit-tested:
 *   1. closed-for-season pools sink to the bottom (never actionable);
 *   2. favorites rise to the top of the remainder;
 *   3. within each group, when a location fix exists, sort nearest-first
 *      (pools with no usable coordinates fall last via an Infinity distance);
 *   4. with no fix, the original (catalog) order is preserved.
 * This subsumes the old `favoritesFirst` (pass `loc = null` for identical output).
 */
export function sortPools(pools: Pool[], loc: LatLng | null, favs: Set<string>): Pool[] {
  return pools
    .map((pool, index) => ({
      pool,
      index,
      dist: loc ? poolDistanceMeters(pool, loc) : 0,
    }))
    .sort((a, b) => {
      const ca = a.pool.closedForSeason ? 1 : 0;
      const cb = b.pool.closedForSeason ? 1 : 0;
      if (ca !== cb) return ca - cb;
      const fa = favs.has(a.pool.poolId) ? 0 : 1;
      const fb = favs.has(b.pool.poolId) ? 0 : 1;
      if (fa !== fb) return fa - fb;
      if (loc && a.dist !== b.dist) return a.dist - b.dist;
      return a.index - b.index; // stable fallback (no fix, or equal distance)
    })
    .map((entry) => entry.pool);
}

/**
 * The nearest reportable pool to `loc` — the one the "Closest: …" banner names.
 * Closed-for-season and coordinate-less pools are excluded. Returns the pool plus
 * its distance in meters, or null when nothing is locatable.
 */
export function nearestPool(
  pools: Pool[],
  loc: LatLng,
): { pool: Pool; meters: number } | null {
  let best: { pool: Pool; meters: number } | null = null;
  for (const pool of pools) {
    if (pool.closedForSeason) continue;
    const meters = poolDistanceMeters(pool, loc);
    if (!Number.isFinite(meters)) continue;
    if (!best || meters < best.meters) best = { pool, meters };
  }
  return best;
}
